<template>
  <UCard
    v-if="variant === 'card' && quota?.hasSite"
    class="rounded-2xl"
    :ui="{ body: 'space-y-3 p-5' }"
  >
    <div class="flex items-start justify-between gap-3">
      <div>
        <p class="text-sm text-[var(--ui-text-muted)]">فضای فایل</p>
        <p class="mt-1 text-lg font-bold tabular-nums text-[var(--arad-text)]">
          <bdi dir="ltr">{{ formatBytes(quota.usedBytes) }}</bdi>
          <span class="text-sm font-medium text-[var(--ui-text-muted)]">
            از <bdi dir="ltr">{{ formatBytes(quota.allowanceBytes) }}</bdi>
          </span>
        </p>
        <p class="text-sm text-[var(--ui-text-muted)]">
          پلن {{ quota.tierLabel }}
        </p>
      </div>
      <UBadge
        :color="badgeColor"
        variant="subtle"
      >
        {{ toFaDigits(usedPercent) }}٪ پر
      </UBadge>
    </div>
    <div class="h-1.5 w-full overflow-hidden rounded-full bg-black/[0.08] dark:bg-white/10">
      <div
        class="h-full rounded-full transition-[width] duration-500 ease-out"
        :class="barClass"
        :style="{ width: `${usedPercent}%` }"
      />
    </div>
    <p class="text-xs leading-5 text-[var(--ui-text-muted)]">
      تصویر سایت، پرونده، فرم‌ها و فایل‌های خصوصی از همین فضا کم می‌شوند. فایل خصوصی فقط با ورود کاربر همان سایت باز می‌شود.
    </p>
  </UCard>

  <div
    v-else-if="quota?.hasSite"
    class="w-full overflow-hidden rounded-2xl bg-white/75 px-2.5 py-2 shadow-[0_1px_1px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.05)] ring-1 ring-black/[0.06] backdrop-blur-xl dark:bg-white/[0.06] dark:shadow-none dark:ring-white/10"
    :class="collapsed ? 'flex justify-center px-1.5' : ''"
    :title="collapsed ? `فضای فایل: ${formatBytes(quota.usedBytes)} از ${formatBytes(quota.allowanceBytes)}` : undefined"
  >
    <span
      v-if="collapsed"
      class="flex size-8 items-center justify-center rounded-xl"
      :class="iconWellClass"
    >
      <UIcon
        name="i-lucide-hard-drive"
        class="size-4"
        :class="iconClass"
      />
    </span>
    <div
      v-else
      class="flex items-center gap-2.5"
    >
      <span
        class="flex size-8 shrink-0 items-center justify-center rounded-xl"
        :class="iconWellClass"
      >
        <UIcon
          name="i-lucide-hard-drive"
          class="size-4"
          :class="iconClass"
        />
      </span>
      <div class="min-w-0 flex-1">
        <div class="flex items-baseline justify-between gap-2">
          <p class="text-[11px] font-medium text-[var(--ui-text-muted)]">
            فضای فایل
          </p>
          <p class="whitespace-nowrap text-[13px] font-semibold tabular-nums tracking-tight text-[var(--arad-text)]">
            <bdi dir="ltr">{{ formatBytes(quota.usedBytes) }}</bdi>
            <span class="text-[11px] font-medium text-[var(--ui-text-muted)]">
              از <bdi dir="ltr">{{ formatBytes(quota.allowanceBytes) }}</bdi>
            </span>
          </p>
        </div>
        <div class="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-black/[0.08] dark:bg-white/10">
          <div
            class="h-full rounded-full transition-[width] duration-500 ease-out"
            :class="barClass"
            :style="{ width: `${usedPercent}%` }"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { toFaDigits } from '~/utils/faDigits'

withDefaults(defineProps<{
  collapsed?: boolean
  variant?: 'card' | 'compact'
}>(), {
  collapsed: false,
  variant: 'compact',
})

const { data } = await useFetch<{
  hasSite: boolean
  tier: string
  tierLabel: string
  allowanceBytes: number
  usedBytes: number
  remainingBytes: number
}>('/api/me/storage-quota', { key: 'storage-quota' })

const quota = computed(() => data.value)

const usedPercent = computed(() => {
  const allowance = quota.value?.allowanceBytes ?? 0
  if (!allowance) return 0
  return Math.min(100, Math.max(0, Math.round(((quota.value?.usedBytes ?? 0) / allowance) * 100)))
})

const level = computed(() => {
  const used = usedPercent.value
  if (used >= 90) return 'full' as const
  if (used >= 75) return 'high' as const
  if (used >= 50) return 'mid' as const
  return 'low' as const
})

const barClass = computed(() => {
  if (level.value === 'full') return 'bg-red-500'
  if (level.value === 'high') return 'bg-orange-500'
  if (level.value === 'mid') return 'bg-yellow-400'
  return 'bg-green-600'
})

const iconWellClass = computed(() => {
  if (level.value === 'full') return 'bg-red-500/12'
  if (level.value === 'high') return 'bg-orange-500/14'
  if (level.value === 'mid') return 'bg-yellow-400/18'
  return 'bg-green-600/12'
})

const iconClass = computed(() => {
  if (level.value === 'full') return 'text-red-600'
  if (level.value === 'high') return 'text-orange-600'
  if (level.value === 'mid') return 'text-yellow-600'
  return 'text-green-700'
})

const badgeColor = computed(() => {
  if (level.value === 'full') return 'error' as const
  if (level.value === 'high' || level.value === 'mid') return 'warning' as const
  return 'success' as const
})

function formatBytes(bytes: number): string {
  const gb = bytes / (1024 ** 3)
  if (gb >= 1) {
    const text = gb >= 10 ? gb.toFixed(0) : gb.toFixed(1)
    return `${toFaDigits(text)} gig`
  }
  const mb = bytes / (1024 ** 2)
  if (mb >= 1) {
    const text = mb >= 10 ? mb.toFixed(0) : mb.toFixed(1)
    return `${toFaDigits(text)} MB`
  }
  return `${toFaDigits(bytes)} بایت`
}
</script>
